import type { FC } from 'react'
import type { ClosePeriodAndWriteAccrualsRequest, PrepareLedgerItem } from 'types/accruals'
import type { Period } from 'types/periods'
import { formatDisplayDate } from 'types/periods'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Card from 'components/@extended/Card'

dayjs.extend(utc)
import Paper from 'components/@extended/Paper'
import DebitCreditBalanceTable from 'components/PeriodClosureDialog/DebitCreditBalanceTable'
import DebitCreditReviewTable from 'components/PeriodClosureDialog/DebitCreditReviewTable'
import { usePrepareLedger } from 'hooks/useAccruals'
import useAuth from 'hooks/useAuth'
import { usePeriods } from 'hooks/usePeriods'
import { useEffect, useState } from 'react'
import { ThemeMode } from 'types/config'
import { postWithTokenWithStatus } from 'hooks/http'

interface AdjustmentValue {
  percentChange: number
  fixedChange: number
  activeType: 'percentage' | 'currency'
  currentBalance: number
  originalBalance: number
}

const DEFAULT_ADJUSTMENT: AdjustmentValue = {
  percentChange: 1.0,
  fixedChange: 0.0,
  activeType: 'percentage' as const,
  currentBalance: 0.0,
  originalBalance: 0.0,
}

const ClosePeriodPage: FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const { token } = useAuth()

  const [activeStep, setActiveStep] = useState(0)
  const [adjustments, setAdjustments] = useState<Record<string, AdjustmentValue>>({})
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [additionalLineItems, setAdditionalLineItems] = useState<PrepareLedgerItem[]>([])
  const [deletedLineItems, setDeletedLineItems] = useState<Set<string>>(new Set())
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null)
  const [periodIdToLabel, setPeriodIdToLabel] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Bulk adjustment state
  const [bulkAdjustmentType, setBulkAdjustmentType] = useState<'debit' | 'credit'>('credit')
  const [bulkAmountType, setBulkAmountType] = useState<'percentage' | 'value'>('percentage')
  const [bulkPercentageBase, setBulkPercentageBase] = useState<'change' | 'balance'>('change')
  const [bulkAmount, setBulkAmount] = useState('0')
  const [bulkLabel, setBulkLabel] = useState('')
  const [bulkAdjustmentDate, setBulkAdjustmentDate] = useState(dayjs.utc())

  const SELECT_PERIODS_STEP = 0
  const ADJUSTMENT_STEP = 1
  const REVIEW_STEP = 2

  const getCompositeId = (item: PrepareLedgerItem) => `${item.allocationTableId}-${item.allocationRowId}`

  // Fetch periods to find the selected period
  const { periods: fetchedPeriods } = usePeriods(token!, null, !!token)
  const rootPeriods = fetchedPeriods?.periods || []

  // Find the selected period and set up period ID to label mapping
  useEffect(() => {
    if (uuid && rootPeriods.length > 0) {
      const findPeriodById = (periods: Period[], id: string): Period | null => {
        for (const period of periods) {
          if (period.id === id) {
            return period
          }
          if (period.children) {
            const found = findPeriodById(period.children, id)
            if (found) return found
          }
        }
        return null
      }

      const found = findPeriodById(rootPeriods, uuid)
      setSelectedPeriod(found)

      // Create period ID to label mapping
      const createFullIdLabelMap = (periodsArray: Period[]): Record<string, string> => {
        const map: Record<string, string> = {}
        const addToMap = (period: Period) => {
          if (period.id) {
            map[period.id] = period.label
          }
          if (period.children) {
            period.children.forEach(addToMap)
          }
        }
        periodsArray.forEach(addToMap)
        return map
      }

      setPeriodIdToLabel(createFullIdLabelMap(rootPeriods))
    }
  }, [uuid, rootPeriods])

  const findAllOpenDescendants = (period: Period): Period[] => {
    let openPeriods: Period[] = []
    if (!period.isClosed) {
      openPeriods.push(period)
    }
    if (period.children) {
      period.children.forEach((child) => {
        openPeriods = [...openPeriods, ...findAllOpenDescendants(child)]
      })
    }
    return openPeriods
  }

  const { ledgerItems, isLoading } = usePrepareLedger({
    token: token || '',
    rootOrganizationId: selectedPeriod?.organizationId || '',
    rootPeriodId: selectedPeriod?.rootPeriodId || '',
    periodIds: selectedPeriod
      ? findAllOpenDescendants(selectedPeriod)
          .map((p) => p.id || '')
          .filter((id) => id !== '')
      : [],
    effectiveDate: selectedPeriod?.endDate ? dayjs.utc(selectedPeriod.endDate).toISOString() : undefined,
    shouldFetch: activeStep >= 1 && selectedPeriod?.rootPeriodId !== '',
  })

  useEffect(() => {
    setActiveStep(0)
    setAdjustments({})
    setNotes({})
    setAdditionalLineItems([])
    setDeletedLineItems(new Set())
    // Reset bulk adjustment form
    setBulkAdjustmentType('credit')
    setBulkAmountType('percentage')
    setBulkPercentageBase('change')
    setBulkAmount('0')
    setBulkLabel('')
    setBulkAdjustmentDate(dayjs.utc())
  }, [uuid])

  useEffect(() => {
    if (ledgerItems) {
      const initialChecked = ledgerItems.reduce(
        (acc, item) => ({
          ...acc,
          [getCompositeId(item)]: true,
        }),
        {}
      )
      setCheckedItems(initialChecked)
    }
  }, [ledgerItems])

  useEffect(() => {
    if (ledgerItems) {
      const initialAdjustments = ledgerItems.reduce(
        (acc, item) => ({
          ...acc,
          [getCompositeId(item)]: {
            percentChange: 1.0,
            fixedChange: item.balance,
            activeType: 'percentage' as const,
            currentBalance: item.balance,
            originalBalance: item.balance,
          },
        }),
        {}
      )
      setAdjustments(initialAdjustments)
    }
  }, [ledgerItems])

  // Filter out deleted items and combine with additional items, then filter zero-value items
  const allLineItems: PrepareLedgerItem[] = [
    ...(ledgerItems ?? []).filter((item) => !deletedLineItems.has(getCompositeId(item))),
    ...additionalLineItems,
  ]

  // Filter out items with zero values (no debit or credit)
  const lineItems: PrepareLedgerItem[] = allLineItems.filter((item) => {
    const compositeId = getCompositeId(item)
    const adjustment = adjustments[compositeId]

    let balanceChange = 0
    if (adjustment) {
      balanceChange = adjustment.currentBalance - item.previousBalance
    } else {
      balanceChange = item.balance - item.previousBalance
    }

    // Keep items that have non-zero balance changes or are additional line items (adjustments)
    return balanceChange !== 0 || additionalLineItems.some((adj) => getCompositeId(adj) === compositeId)
  })

  // Count filtered items
  const filteredItemsCount = allLineItems.length - lineItems.length

  const handleNext = async () => {
    switch (activeStep) {
      case SELECT_PERIODS_STEP:
        setActiveStep(ADJUSTMENT_STEP)
        break
      case ADJUSTMENT_STEP:
        setActiveStep(REVIEW_STEP)
        break
      case REVIEW_STEP:
        if (selectedPeriod && token) {
          setIsSubmitting(true)
          const request: ClosePeriodAndWriteAccrualsRequest = {
            rootOrganizationId: selectedPeriod.organizationId || '',
            rootPeriodId: selectedPeriod.rootPeriodId || '',
            periodsToClose: findAllOpenDescendants(selectedPeriod)
              .map((p) => p.id || '')
              .filter((id) => id !== ''),
            effectiveDate: dayjs.utc(selectedPeriod.endDate).toISOString(),
            accrualLedgerEntries: lineItems
              .filter((item) => checkedItems[getCompositeId(item)])
              .map((item) => {
                const compositeId = getCompositeId(item)

                return {
                  userId: item.userId,
                  rootOrganizationId: item.rootOrganizationId,
                  organizationId: item.organizationId,
                  ledgerAccountId: item.ledgerAccountId,
                  planId: item.planId,
                  planRevision: item.planRevision,
                  allocationTableId: item.allocationTableId,
                  allocationTableName: item.allocationTableName,
                  allocationRowId: item.allocationRowId,
                  allocationRowName: item.allocationRowName,
                  rootPeriodId: item.rootPeriodId,
                  periodId: item.periodId,
                  balance: getAdjustedBalance(item),
                  notes: notes[compositeId] || undefined,
                }
              }),
          }

          try {
            const response = await postWithTokenWithStatus('accruals/close-periods', token, request)
            if (response.status === 200) {
              // Success - navigate back to close periods page
              navigate('/admin/close-periods')
            } else {
              throw new Error(response.data?.error || 'Failed to close periods and write accruals')
            }
          } catch (error) {
            console.error('Error closing period:', error)
            // Could add error handling UI here
          } finally {
            setIsSubmitting(false)
          }
        }
        break
    }
  }

  const handleBack = () => {
    switch (activeStep) {
      case ADJUSTMENT_STEP:
        setActiveStep(SELECT_PERIODS_STEP)
        break
      case REVIEW_STEP:
        setActiveStep(ADJUSTMENT_STEP)
        break
    }
  }

  const handleCancel = () => {
    navigate('/admin/close-periods')
  }

  const handleAddLineItem = (
    baseItem: PrepareLedgerItem,
    adjustmentAmount: number,
    lineItemLabel: string,
    adjustmentNotes?: string
  ) => {
    const newLineItem: PrepareLedgerItem = {
      ...baseItem,
      allocationTableId: '00000000-0000-0000-0000-000000000000',
      allocationTableName: 'Adjustment',
      allocationRowId: crypto.randomUUID(),
      allocationRowName: lineItemLabel,
      balance: adjustmentAmount,
      previousBalance: 0,
    }

    setAdditionalLineItems((prev) => [...prev, newLineItem])

    const compositeId = getCompositeId(newLineItem)
    setAdjustments((prev) => ({
      ...prev,
      [compositeId]: {
        percentChange: 1.0,
        fixedChange: adjustmentAmount,
        activeType: 'currency' as const,
        currentBalance: adjustmentAmount,
        originalBalance: 0,
      },
    }))

    // Automatically check the new adjustment item
    setCheckedItems((prev) => ({
      ...prev,
      [compositeId]: true,
    }))

    if (adjustmentNotes) {
      setNotes((prev) => ({
        ...prev,
        [compositeId]: adjustmentNotes,
      }))
    }
  }

  const handleDeleteLineItem = (itemToDelete: PrepareLedgerItem) => {
    const compositeId = getCompositeId(itemToDelete)

    // Check if it's a custom adjustment line item
    if (additionalLineItems.some((item) => getCompositeId(item) === compositeId)) {
      // Remove from additionalLineItems
      setAdditionalLineItems((prev) => prev.filter((item) => getCompositeId(item) !== compositeId))
    } else {
      // For original ledger items, add to deleted set
      setDeletedLineItems((prev) => new Set([...prev, compositeId]))
    }

    // Clean up related state for this item
    setAdjustments((prev) => {
      const newAdjustments = { ...prev }
      delete newAdjustments[compositeId]
      return newAdjustments
    })

    setNotes((prev) => {
      const newNotes = { ...prev }
      delete newNotes[compositeId]
      return newNotes
    })

    setCheckedItems((prev) => {
      const newChecked = { ...prev }
      delete newChecked[compositeId]
      return newChecked
    })
  }

  const handleBulkAdjustment = () => {
    if (!bulkLabel.trim() || bulkAmount === '' || parseFloat(bulkAmount) === 0) return

    // Group all current line items by user
    const userGroups: Record<string, PrepareLedgerItem[]> = {}
    lineItems.forEach((item) => {
      const userKey = `${item.userId}-${item.firstName}-${item.lastName}`
      if (!userGroups[userKey]) {
        userGroups[userKey] = []
      }
      userGroups[userKey].push(item)
    })

    // For each user, calculate their remaining credit sum and create a bulk adjustment
    Object.entries(userGroups).forEach(([userKey, userItems]) => {
      if (userItems.length === 0) return

      // Calculate base amount for percentage calculation
      let baseAmount = 0
      if (bulkAmountType === 'percentage') {
        if (bulkPercentageBase === 'change') {
          // Calculate remaining credit sum (change from previous balance)
          baseAmount = userItems.reduce((sum, item) => {
            const compositeId = getCompositeId(item)
            const adjustment = adjustments[compositeId]

            let balanceChange = 0
            if (adjustment) {
              balanceChange = adjustment.currentBalance - item.previousBalance
            } else {
              balanceChange = item.balance - item.previousBalance
            }

            return sum + (balanceChange > 0 ? balanceChange : 0)
          }, 0)
        } else {
          // Calculate running balance (current balance)
          baseAmount = userItems.reduce((sum, item) => {
            const compositeId = getCompositeId(item)
            const adjustment = adjustments[compositeId]

            const currentBalance = adjustment ? adjustment.currentBalance : item.balance
            return sum + (currentBalance > 0 ? currentBalance : 0)
          }, 0)
        }
      }

      if (bulkAmountType === 'percentage' && baseAmount === 0) return

      // Calculate the adjustment amount
      let adjustmentAmount = 0
      if (bulkAmountType === 'percentage') {
        adjustmentAmount = baseAmount * (parseFloat(bulkAmount) / 100)
      } else {
        adjustmentAmount = parseFloat(bulkAmount)
      }

      // Apply debit/credit type
      if (bulkAdjustmentType === 'debit') {
        adjustmentAmount = -adjustmentAmount
      }

      // Use the first item of the user as the base for creating the adjustment
      const baseItem = userItems[0]
      handleAddLineItem(baseItem, adjustmentAmount, bulkLabel, `Bulk ${bulkAdjustmentType} adjustment`)
    })

    // Reset bulk form
    setBulkAmount('0')
    setBulkLabel('')
    setBulkAdjustmentDate(dayjs.utc())
  }

  const getAdjustment = (row: PrepareLedgerItem): AdjustmentValue => {
    const compositeId = getCompositeId(row)
    return adjustments[compositeId] || { ...DEFAULT_ADJUSTMENT, fixedChange: row.balance }
  }

  const getAdjustedBalance = (row: PrepareLedgerItem) => {
    const adjustment = getAdjustment(row)
    return adjustment.currentBalance
  }

  const formatMoney = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const getTotalCredits = () => {
    const total = lineItems
      .filter((item) => checkedItems[getCompositeId(item)] ?? true)
      .reduce((sum, item) => {
        const change = getAdjustment(item).currentBalance - item.previousBalance
        return sum + (change > 0 ? change : 0)
      }, 0)
    return formatMoney(total)
  }

  const getTotalDebits = () => {
    const total = lineItems
      .filter((item) => checkedItems[getCompositeId(item)] ?? true)
      .reduce((sum, item) => {
        const change = getAdjustment(item).currentBalance - item.previousBalance
        return sum + (change < 0 ? Math.abs(change) : 0)
      }, 0)
    return formatMoney(total)
  }

  const renderStepContent = () => {
    const periodsColumns = [
      {
        id: 'period_label',
        label: `Period (${selectedPeriod ? findAllOpenDescendants(selectedPeriod).length : 0})`,
        minWidth: 170,
      },
      {
        id: 'period_startDate',
        label: 'Start Date',
        minWidth: 120,
        format: (value: string) => formatDisplayDate(value),
      },
      {
        id: 'period_endDate',
        label: 'End Date',
        minWidth: 120,
        format: (value: string) => formatDisplayDate(value),
      },
    ]

    const PeriodsTable = () => (
      <TableContainer
        component={Paper}
        sx={{
          height: '100%',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead
            sx={{
              '& th': {
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `2px solid ${theme.palette.divider} !important`,
                userSelect: 'none',
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  outline: 'none',
                },
              },
            }}
          >
            <TableRow>
              {periodsColumns.map((column) => (
                <TableCell
                  key={`periods-column-${column.id}-cell`}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    position: 'sticky !important',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedPeriod &&
              findAllOpenDescendants(selectedPeriod)
                .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                .map((row) => (
                  <TableRow key={`periods-row-${row.id}`}>
                    {periodsColumns.map((column) => {
                      const value = row[column.id.replace('period_', '') as keyof Period]
                      return (
                        <TableCell key={`periods-column-${column.id}-cell`}>
                          {column.format ? column.format(String(value)) : String(value)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    )

    switch (activeStep) {
      case SELECT_PERIODS_STEP:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Typography variant="body1" sx={{ mb: 2, flexShrink: 0 }}>
              The following periods will be closed and their most recent approved accrual progress will be written to
              the accrual ledger. Additionally, any approved progress for parent periods (e.g., Quarters, Half-Years,
              and Fiscal Year) will also be written to the accrual ledger.
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <PeriodsTable />
            </Box>
          </Box>
        )
      case ADJUSTMENT_STEP:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Adjust balances using debit/credit entries. Click the plus icon in the Actions column to add adjustment
                line items.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Users are grouped together with alternating background colors. Debits reduce balances, credits increase
                them.
                {filteredItemsCount > 0 && (
                  <>
                    {' '}
                    {filteredItemsCount} line item{filteredItemsCount !== 1 ? 's' : ''} with zero values have been
                    automatically filtered out.
                  </>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  flex: '1 1 0',
                  maxWidth: 'calc(100% - 320px)',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <DebitCreditBalanceTable
                  lineItems={lineItems}
                  adjustments={adjustments}
                  notes={notes}
                  onAddLineItem={handleAddLineItem}
                  onDeleteLineItem={handleDeleteLineItem}
                  onUpdateNotes={(compositeId, newNotes) => {
                    setNotes((prev) => ({
                      ...prev,
                      [compositeId]: newNotes,
                    }))
                  }}
                  periodIdToLabel={periodIdToLabel}
                  isLoading={isLoading}
                />
              </Box>
              <Box sx={{ width: 300, flexShrink: 0 }}>
                <Card variant="outlined" sx={{ height: 'fit-content' }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                      Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Credits:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          ${getTotalCredits()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Debits:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          ${getTotalDebits()}
                        </Typography>
                      </Box>
                      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            Net Change:
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            $
                            {(
                              parseFloat(getTotalCredits().replace(/,/g, '')) -
                              parseFloat(getTotalDebits().replace(/,/g, ''))
                            ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>

                {/* Bulk Adjustments Card */}
                <Card variant="outlined" sx={{ height: 'fit-content', mt: 2 }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                      Bulk Adjustments
                    </Typography>

                    <ToggleButtonGroup
                      value={bulkAdjustmentType}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue !== null) {
                          setBulkAdjustmentType(newValue)
                        }
                      }}
                      aria-label="bulk adjustment type"
                      size="small"
                      sx={{ mb: 2, width: '100%' }}
                    >
                      <ToggleButton value="debit" aria-label="Debit" sx={{ width: '50%' }}>
                        <Typography>Debit</Typography>
                      </ToggleButton>
                      <ToggleButton value="credit" aria-label="Credit" sx={{ width: '50%' }}>
                        <Typography>Credit</Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                      value={bulkAmountType}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue !== null) {
                          setBulkAmountType(newValue)
                        }
                      }}
                      aria-label="bulk amount type"
                      size="small"
                      sx={{ mb: 2, width: '100%' }}
                    >
                      <ToggleButton value="percentage" aria-label="Percentage" sx={{ width: '50%' }}>
                        <Typography>Percentage</Typography>
                      </ToggleButton>
                      <ToggleButton value="value" aria-label="Value" sx={{ width: '50%' }}>
                        <Typography>Fixed Value</Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {bulkAmountType === 'percentage' && (
                      <ToggleButtonGroup
                        value={bulkPercentageBase}
                        exclusive
                        onChange={(_, newValue) => {
                          if (newValue !== null) {
                            setBulkPercentageBase(newValue)
                          }
                        }}
                        aria-label="percentage base"
                        size="small"
                        sx={{ mb: 2, width: '100%' }}
                      >
                        <ToggleButton value="change" aria-label="Against Change" sx={{ width: '50%' }}>
                          <Typography variant="caption">vs Change</Typography>
                        </ToggleButton>
                        <ToggleButton value="balance" aria-label="Against Balance" sx={{ width: '50%' }}>
                          <Typography variant="caption">vs Balance</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}

                    <TextField
                      label={bulkAmountType === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                      type="number"
                      value={bulkAmount}
                      onChange={(e) => setBulkAmount(e.target.value)}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          '&.MuiInputLabel-shrink': {
                            background: theme.palette.background.paper,
                          },
                        },
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '21px',
                        },
                      }}
                      inputProps={{ min: 0, step: bulkAmountType === 'percentage' ? 1 : 0.01 }}
                    />

                    <TextField
                      label="Label"
                      value={bulkLabel}
                      onChange={(e) => setBulkLabel(e.target.value)}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          '&.MuiInputLabel-shrink': {
                            background: theme.palette.background.paper,
                          },
                        },
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '21px',
                        },
                      }}
                      placeholder="Enter a label for this bulk adjustment..."
                      required
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Adjustment Date"
                        value={bulkAdjustmentDate}
                        onChange={(newValue) => {
                          if (newValue) {
                            const dateString = newValue.format('YYYY-MM-DD')
                            const utcDate = dayjs.utc(dateString)
                            setBulkAdjustmentDate(utcDate)
                          } else {
                            setBulkAdjustmentDate(dayjs.utc())
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            InputLabelProps: {
                              shrink: true,
                              sx: {
                                '&.MuiInputLabel-shrink': {
                                  background: theme.palette.background.paper,
                                },
                              },
                            },
                            sx: {
                              mb: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '21px',
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <Button
                      onClick={handleBulkAdjustment}
                      variant="contained"
                      fullWidth
                      disabled={!bulkLabel.trim() || bulkAmount === '' || parseFloat(bulkAmount) === 0}
                    >
                      Apply Bulk Adjustment
                    </Button>
                  </Box>
                </Card>
              </Box>
            </Box>
          </Box>
        )
      case REVIEW_STEP:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Please review all information before confirming the period closure.
                {filteredItemsCount > 0 && (
                  <>
                    {' '}
                    {filteredItemsCount} line item{filteredItemsCount !== 1 ? 's' : ''} with zero values have been
                    automatically filtered out.
                  </>
                )}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color:
                    theme.palette.mode === ThemeMode.LIGHT ? theme.palette.warning.darker : theme.palette.warning.main,
                }}
              >
                Warning: These balances will enter the approval process and will overwrite any unapproved balances in
                the ledger. Once approved, these balances will be permanent and cannot be removed or adjusted.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  flex: '1 1 0',
                  maxWidth: 'calc(100% - 320px)',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <DebitCreditReviewTable
                  lineItems={lineItems}
                  adjustments={adjustments}
                  checkedItems={checkedItems}
                  notes={notes}
                  periodIdToLabel={periodIdToLabel}
                  isLoading={isLoading}
                />
              </Box>
              <Box sx={{ width: 300, flexShrink: 0 }}>
                <Card variant="outlined" sx={{ height: 'fit-content' }}>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                      Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Credits:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          ${getTotalCredits()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Debits:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                          ${getTotalDebits()}
                        </Typography>
                      </Box>
                      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            Net Change:
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            $
                            {(
                              parseFloat(getTotalCredits().replace(/,/g, '')) -
                              parseFloat(getTotalDebits().replace(/,/g, ''))
                            ).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Box>
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Box sx={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column', p: 3, overflow: 'hidden' }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Close Period
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Select Periods</StepLabel>
        </Step>
        <Step>
          <StepLabel>Adjust Balances</StepLabel>
        </Step>
        <Step>
          <StepLabel>Review</StepLabel>
        </Step>
      </Stepper>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {renderStepContent()}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, flexShrink: 0 }}>
        <Button onClick={handleCancel} variant="contained" color="warning">
          Cancel
        </Button>
        <div>
          {activeStep > SELECT_PERIODS_STEP && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          <Button onClick={handleNext} variant="contained" color="primary" disabled={isSubmitting}>
            {activeStep === REVIEW_STEP ? (isSubmitting ? 'Closing...' : 'Confirm') : 'Next'}
          </Button>
        </div>
      </Box>
    </Box>
  )
}

export default ClosePeriodPage
